"use client";

/**
 * AccessDenied Component
 * REQ-008: Goal Dashboard - Access denied fallback
 *
 * Displayed when a user lacks tier-level access to a protected page.
 * Used as fallback for TierGuard component.
 */

import type * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ShieldWarningIcon } from "@phosphor-icons/react/dist/ssr/ShieldWarning";
import { Link as RouterLink } from "react-router-dom";

import { paths } from "@/config/paths";

/**
 * AccessDenied props interface
 */
export interface AccessDeniedProps {
  /** Custom message to display (overrides default) */
  message?: string;
  /** Custom data-testid */
  "data-testid"?: string;
}

/**
 * AccessDenied - Fallback component for tier-restricted content
 *
 * @example
 * ```tsx
 * <TierGuard minTier={2} fallback={<AccessDenied />}>
 *   <TeamDashboard />
 * </TierGuard>
 *
 * // With custom message
 * <TierGuard minTier={4} fallback={<AccessDenied message="Executive access required." />}>
 *   <CompanyDashboard />
 * </TierGuard>
 * ```
 */
export function AccessDenied({
  message,
  "data-testid": dataTestId = "access-denied",
}: AccessDeniedProps): React.JSX.Element {
  const defaultMessage =
    "You don't have the required permissions to view this page. Please contact your administrator if you believe this is an error.";

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        py: 6,
      }}
    >
      <Container maxWidth="sm">
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "error.50",
              color: "error.main",
            }}
          >
            <ShieldWarningIcon size={48} weight="duotone" />
          </Box>

          <Typography variant="h4" fontWeight={600}>
            Access Restricted
          </Typography>

          <Typography variant="body1" color="text.secondary">
            {message || defaultMessage}
          </Typography>

          <Button
            component={RouterLink}
            to={paths.dashboard.index}
            variant="contained"
            size="large"
            data-testid="access-denied-back-btn"
          >
            Back to Dashboard
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}

export default AccessDenied;
